import { AlertTriangle, ArrowRight, ShieldCheck } from "lucide-react";
import { useCallback } from "react";
import { commandCenterApi } from "../../api/command-center.js";
import type { ReportingOverview } from "../../api/types.js";
import {
  coverageGapsHref,
  handleInternalNavigation,
  pendingFindingsHref,
  safeConsoleHref,
} from "../../app/routing.js";
import { InlineError, LoadingTable } from "../../components/AsyncState.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { formatDateTime, formatInteger, formatPercent } from "../../lib/format.js";

export function MetricValue({ value, unit }: { value: number | null; unit: "count" | "percent" }) {
  if (value === null) return <span className="unavailable-value">Unavailable</span>;
  return <>{unit === "percent" ? formatPercent(value) : formatInteger(value)}</>;
}

function AvailableValue({ value }: { value: number | null }) {
  return value === null ? (
    <span className="unavailable-value">Unavailable</span>
  ) : (
    formatInteger(value)
  );
}

function ExecutiveCockpit({
  data,
  navigate,
}: {
  data: ReportingOverview;
  navigate: (path: string) => void;
}) {
  const severity = data.distributions.find((distribution) => distribution.id === "severity");
  const maximumSeverity = Math.max(1, ...(severity?.items.map((item) => item.count) ?? [1]));
  const attention = data.metrics.filter((metric) =>
    ["replay-failures", "coverage-gaps", "needs-human", "active-remediations"].includes(metric.id),
  );
  const reconciliation = data.projection.reconciliation;
  const needsHuman = data.metrics.find((metric) => metric.id === "needs-human")?.value;
  const replayFailures = data.metrics.find((metric) => metric.id === "replay-failures")?.value;
  const actionableFindings =
    data.metrics.find((metric) => metric.id === "actionable-findings")?.value ?? null;
  const attentionCaption = (metric: (typeof attention)[number]): string | null => {
    if (metric.limitation) return metric.limitation;
    if (metric.id === "needs-human") return "review decisions waiting";
    if (metric.id === "active-remediations") return "fix plans underway";
    if (metric.id === "replay-failures") return "evidence must be repaired";
    if (metric.id === "coverage-gaps") return "files still need review";
    return null;
  };
  const scanScope = data.projection.scope.scanId ?? undefined;
  const reviewQueueHref = pendingFindingsHref(scanScope);
  const coverageHref = coverageGapsHref(scanScope);

  return (
    <section className="executive-cockpit" aria-labelledby="executive-cockpit-heading">
      <div className="executive-cockpit__heading">
        <div>
          <span className="executive-cockpit__eyebrow">Proof cockpit</span>
          <h2 id="executive-cockpit-heading">
            {needsHuman == null
              ? "Trust is still being verified."
              : needsHuman > 0
                ? `${formatInteger(needsHuman)} decisions are blocking trust.`
                : "No human decisions are blocking trust."}
          </h2>
          <p className="executive-decision-line">
            Noise becomes a decision path: confirmed exposure, replay-backed proof, and the next
            action a reviewer can take without guessing.
          </p>
          <p className="executive-impact-line">
            <strong>
              {actionableFindings == null
                ? reconciliation.confirmed == null
                  ? "Reviewed decisions are still unavailable."
                  : `${formatInteger(reconciliation.confirmed)} findings have a reviewed decision.`
                : `${formatInteger(actionableFindings)} findings are in the evidence set.`}
            </strong>
            <span>
              {reconciliation.confirmed == null
                ? "Confirmed count unavailable."
                : `${formatInteger(reconciliation.confirmed)} confirmed.`}{" "}
              {replayFailures === 0
                ? "Every available citation replayed cleanly."
                : replayFailures == null
                  ? "Replay status is unavailable."
                  : `${formatInteger(replayFailures)} replay issue${replayFailures === 1 ? "" : "s"} must be resolved before trust claims.`}
            </span>
          </p>
          <div className="executive-cockpit__actions">
            <a
              className="button button--primary"
              href={reviewQueueHref}
              onClick={(event) => handleInternalNavigation(event, navigate)}
            >
              Open decision queue
              <ArrowRight aria-hidden="true" size={16} />
            </a>
            <a
              className="button button--ghost"
              href={coverageHref}
              onClick={(event) => handleInternalNavigation(event, navigate)}
            >
              Show coverage gaps
            </a>
          </div>
          <ol
            className="decision-story"
            data-testid="decision-story"
            aria-label="Exposure, proof quality, and action"
          >
            <li data-testid="decision-story-exposure">
              <span>Exposure</span>
              <strong>
                <AvailableValue
                  value={
                    data.metrics.find((metric) => metric.id === "confirmed-critical-high")?.value ??
                    reconciliation.confirmed
                  }
                />
              </strong>
              <small>Confirmed critical/high findings</small>
            </li>
            <li data-testid="decision-story-proof">
              <span>Proof quality</span>
              <strong>
                {replayFailures === 0
                  ? "Replay held"
                  : replayFailures == null
                    ? "Unavailable"
                    : "Replay issues"}
              </strong>
              <small>Trust only survives replay</small>
            </li>
            <li data-testid="decision-story-action">
              <span>Action</span>
              <strong>
                <AvailableValue value={needsHuman ?? null} />
              </strong>
              <small>Reviewer decisions waiting</small>
            </li>
          </ol>
        </div>
        <section className="assurance-orbit" aria-label="Live assurance signal">
          <div className="assurance-orbit__rings" aria-hidden="true">
            <i />
            <i />
            <i />
            <span className="assurance-orbit__node assurance-orbit__node--confirmed" />
            <span className="assurance-orbit__node assurance-orbit__node--review" />
            <span className="assurance-orbit__node assurance-orbit__node--replay" />
          </div>
          <div className="assurance-orbit__center">
            <ShieldCheck aria-hidden="true" size={24} />
            <strong>Evidence pulse</strong>
            <span>3 live signals</span>
          </div>
          <dl className="assurance-orbit__legend">
            <div>
              <dt>
                <span
                  className="assurance-orbit__legend-dot assurance-orbit__legend-dot--confirmed"
                  aria-hidden="true"
                />
                Confirmed
              </dt>
              <dd>
                <AvailableValue value={reconciliation.confirmed} />
              </dd>
            </div>
            <div>
              <dt>
                <span
                  className="assurance-orbit__legend-dot assurance-orbit__legend-dot--review"
                  aria-hidden="true"
                />
                Awaiting decision
              </dt>
              <dd>
                <AvailableValue value={needsHuman ?? null} />
              </dd>
            </div>
            <div>
              <dt>
                <span
                  className="assurance-orbit__legend-dot assurance-orbit__legend-dot--replay"
                  aria-hidden="true"
                />
                Replay issues
              </dt>
              <dd>
                <AvailableValue value={replayFailures ?? null} />
              </dd>
            </div>
          </dl>
          <div className="executive-cockpit__freshness">
            <span>Evidence set</span>
            <strong title={data.revision}>Current replayed snapshot</strong>
            <small>{formatDateTime(data.generatedAt)}</small>
          </div>
        </section>
      </div>

      <div className="executive-cockpit__grid">
        <section className="executive-panel" aria-labelledby="risk-profile-heading">
          <div className="executive-panel__heading">
            <h3 id="risk-profile-heading">Risk at a glance</h3>
            <small>Canonical findings · all visible statuses</small>
          </div>
          {severity ? (
            <ul className="risk-profile">
              {severity.items.map((item) => (
                <li key={item.id}>
                  <a
                    href={item.queryHref}
                    onClick={(event) => handleInternalNavigation(event, navigate)}
                  >
                    <span>{item.label}</span>
                    <span className={`risk-profile__track risk-profile__track--${item.id}`}>
                      <span
                        style={
                          { "--risk-ratio": item.count / maximumSeverity } as React.CSSProperties
                        }
                      />
                    </span>
                    <strong>{formatInteger(item.count)}</strong>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="unavailable-value">Unavailable</p>
          )}
        </section>

        <section className="executive-panel" aria-labelledby="proof-health-heading">
          <div className="executive-panel__heading">
            <h3 id="proof-health-heading">Evidence you can trust</h3>
            <small>Never substitutes unavailable with zero</small>
          </div>
          <dl className="proof-health">
            <div>
              <dt>Replay matched</dt>
              <dd>
                <AvailableValue value={reconciliation.replayMatchedCandidates} />
              </dd>
            </div>
            <div>
              <dt>Challenged / refuted</dt>
              <dd>
                <AvailableValue value={reconciliation.challengedOrRefuted} />
              </dd>
            </div>
            <div>
              <dt>Confirmed</dt>
              <dd>
                <AvailableValue value={reconciliation.confirmed} />
              </dd>
            </div>
            <div>
              <dt>Verified fixes</dt>
              <dd>
                <AvailableValue value={reconciliation.verifiedFixes} />
              </dd>
            </div>
          </dl>
        </section>

        <section
          className="executive-panel executive-panel--attention"
          aria-labelledby="attention-heading"
        >
          <div className="executive-panel__heading">
            <h3 id="attention-heading">Your action queue</h3>
            <small>Click any row for the governed result set</small>
          </div>
          <ul className="attention-queue">
            {attention.map((metric) => {
              const href = safeConsoleHref(metric.queryHref);
              const caption = attentionCaption(metric);
              const row = (
                <>
                  <span>
                    <i aria-hidden="true" />
                    {metric.label}
                    {caption ? <small>{caption}</small> : null}
                  </span>
                  <strong>
                    <MetricValue value={metric.value} unit={metric.unit} />
                  </strong>
                  <ArrowRight aria-hidden="true" size={14} />
                </>
              );
              return (
                <li key={metric.id}>
                  {href ? (
                    <a
                      href={href}
                      title={caption ?? undefined}
                      onClick={(event) => handleInternalNavigation(event, navigate)}
                    >
                      {row}
                    </a>
                  ) : (
                    <div title={caption ?? undefined}>{row}</div>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      </div>
    </section>
  );
}

export function OverviewPage({
  navigate,
  selectedScanId,
}: {
  navigate: (path: string) => void;
  selectedScanId?: string | undefined;
}) {
  const loadOverview = useCallback(
    (signal: AbortSignal) => commandCenterApi.getOverview(selectedScanId, signal),
    [selectedScanId],
  );
  const overview = useApiResource(loadOverview);

  return (
    <section className="page overview-page" aria-labelledby="overview-heading">
      <h1 id="overview-heading" className="sr-only">
        Overview
      </h1>
      {overview.isLoading && !overview.data ? <LoadingTable columns={4} rows={3} /> : null}
      {overview.error && !overview.data ? (
        <section
          className="page-section reporting-unavailable"
          aria-labelledby="metrics-unavailable"
        >
          <AlertTriangle aria-hidden="true" size={18} />
          <div>
            <h2 id="metrics-unavailable">Verified reporting is unavailable</h2>
            <p>
              No totals are shown because the tenant-scoped reporting contract did not answer. The
              console will not substitute locally aggregated or cross-tenant counts.
            </p>
            <InlineError error={overview.error} onRetry={overview.refresh} />
          </div>
        </section>
      ) : null}
      {overview.data ? <ExecutiveCockpit data={overview.data} navigate={navigate} /> : null}
    </section>
  );
}
